import React from "react";

import { faBrain } from "@fortawesome/pro-regular-svg-icons/faBrain";
import { faFlag } from "@fortawesome/pro-solid-svg-icons";
import { faArrowsRotate } from "@fortawesome/pro-solid-svg-icons/faArrowsRotate";
import { faRotateLeft } from "@fortawesome/pro-solid-svg-icons/faRotateLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Button, EmailBody } from "@mapmycustomers/ui";

import EmailContext from "@app/component/email/creation/EmailContext";
import ReportPopover from "@app/component/ReportPopover";

import styles from "./GeneratedContent.module.scss";
import messages from "./messages";

interface GeneratedContentProps {
  canRegenerate: boolean;
  content: string;
  generating: boolean;
  onRegenerate: () => void;
  onReport: (comment?: string) => void;
  onReportOpen?: () => void;
  onUndo?: () => void;
  showActionButtons: boolean;
}

const GeneratedContent = ({
  canRegenerate,
  content,
  generating,
  onRegenerate,
  onReport,
  onReportOpen,
  onUndo,
  showActionButtons,
}: GeneratedContentProps) => {
  const intl = useIntl();
  const {
    dynamicVariables,
    dynamicVariablesSupported,
    entityType,
    onResourceDownload,
    resourceGetter,
  } = useNonNullableContext(EmailContext);

  return (
    <div className={styles.container}>
      {generating ? (
        <div className={styles.thinkingContainer}>
          <FontAwesomeIcon className={styles.brainIcon} icon={faBrain} />
          <span className={styles.thinkingText}>{intl.formatMessage(messages.thinking)}</span>
        </div>
      ) : (
        <EmailBody
          className={styles.body}
          dynamicVariables={dynamicVariablesSupported ? dynamicVariables : undefined}
          editableClassName={styles.editable}
          entityType={entityType}
          onResourceDownload={onResourceDownload}
          readOnly
          resourceGetter={resourceGetter}
          value={content}
        />
      )}

      {showActionButtons && (
        <div className={styles.actionButtons}>
          <Tooltip title={intl.formatMessage(messages.undoTooltip)}>
            <Button disabled={!onUndo} iconOnly onClick={onUndo} size="small">
              <FontAwesomeIcon icon={faRotateLeft} />
            </Button>
          </Tooltip>
          <Tooltip title={intl.formatMessage(messages.regenerateTooltip)}>
            <Button disabled={!canRegenerate} iconOnly onClick={onRegenerate} size="small">
              <FontAwesomeIcon icon={faArrowsRotate} />
            </Button>
          </Tooltip>
          <ReportPopover onSubmit={onReport}>
            <Tooltip title={intl.formatMessage(messages.reportTooltip)}>
              <Button iconOnly onClick={onReportOpen} size="small">
                <FontAwesomeIcon icon={faFlag} />
              </Button>
            </Tooltip>
          </ReportPopover>
        </div>
      )}
    </div>
  );
};

export default GeneratedContent;
